import React from 'react';
import { Button } from 'react-covideo-common';
import styled from 'styled-components/macro';
import { NewModal } from 'lib/components';
import { theme } from 'lib/style';
import { useDeleteVideoMutation } from 'lib/api/videos/useDeleteVideoMutation';
import { useUpdateVideoArchiveStatusMutation } from 'lib/api/videos/useUpdateVideoArchiveStatusMutation';
import { useAuth, VideoListAutomotiveItem } from 'lib/context';
import { VideoData } from 'lib/hooks';
import { DEFAULT_COMPANY_FOLDER } from 'lib/const/Folders';
import { Folder } from 'lib/api/folders/getFolders';
import { useDeleteVideoWithROMutation } from 'lib/api/videos/useDeleteVideoWithROMutation';
import { useGTMAnalytics } from 'lib/hooks/GTM/useGTMAnalytics';
import { GTMVideoInteractionActions } from 'lib/hooks/GTM/actions/video';
import { useTranslation } from 'react-i18next';
import { successToast } from '../toasts/success';

const Content = styled.div`
  line-height: 28px;
  color: #272a32;
`;

const Footer = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;
const isVideoListAutomotiveItem = (
  video: VideoListAutomotiveItem | VideoData
): video is VideoListAutomotiveItem => {
  return (video as VideoListAutomotiveItem).videoRequest !== undefined;
};

type Props = {
  handleModalClose: (shouldRefresh?: boolean) => void;
  content: any;
  isCompanyVideo: boolean;
  video: VideoListAutomotiveItem[] | VideoData[];
  isVideoWithRO?: boolean;
  isMultipleVideos?: boolean;
  currentFolder?: Folder | null;
  isFolderShared?: boolean;
};

export const DeleteVideoModal = (props: Props) => {
  const { t } = useTranslation();
  const {
    handleModalClose,
    content,
    isVideoWithRO,
    video,
    currentFolder,
    isMultipleVideos,
    isFolderShared,
    isCompanyVideo,
  } = props;

  const [isDisabled, setIsDisabled] =
    React.useState(false); /** SUS-973 changes **/
  const { userData } = useAuth();

  const { trackGTMVideoInteractionEvent } = useGTMAnalytics();

  const { isAutomotive } = userData;

  const areAllVideosArchived = (
    video as Array<VideoListAutomotiveItem | VideoData>
  ).every(v => v.isArchived === 1);

  const { mutateAsync: deleteVideos } = useDeleteVideoMutation();
  const { mutateAsync: deleteVideoWithRO } = useDeleteVideoWithROMutation();
  const { mutateAsync: updateVideoArchiveStatus } =
    useUpdateVideoArchiveStatusMutation();

  const deleteGTMHandler = (action: GTMVideoInteractionActions) => {
    if (video.length === 1) {
      trackGTMVideoInteractionEvent?.({
        action: action,
        videoAttributes: {
          videoId: Number(video[0].id),
          videoType: isVideoListAutomotiveItem(video[0])
            ? video[0]?.videoRequest?.videoType
            : undefined,
          videoCustomerName: isVideoListAutomotiveItem(video[0])
            ? !!video[0]?.videoRequest?.customerName
            : false,
          videoLength: video[0].videoLength,
          videoTitle: video[0].title,
        },
      });
    } else {
      trackGTMVideoInteractionEvent?.({
        action: action,
        videoIds: video.map(v => Number(v.id)),
      });
    }
  };

  const handleDeleteSubmitSelected = async (
    selectedVideosData: VideoListAutomotiveItem[]
  ) => {
    const hasUserCopyAccess = currentFolder?.access === 'copy';
    const hasUserViewAccess = currentFolder?.access === 'view';
    if (isAutomotive && currentFolder?.name !== DEFAULT_COMPANY_FOLDER) {
      try {
        const deletePromises = selectedVideosData
          .filter(video => video?.videoRequest?.videoRequestId)
          .map(video => deleteVideoWithRO(video.videoRequest.videoRequestId));

        await Promise.all(deletePromises);

        successToast({
          title: `Videos successfully moved to recently deleted bin`,
        });
      } catch (_) {}
    } else {
      await handleDeleteSubmit(
        selectedVideosData.map((video: VideoListAutomotiveItem) => video.id),
        isFolderShared
          ? hasUserCopyAccess || hasUserViewAccess
          : currentFolder?.name === DEFAULT_COMPANY_FOLDER
      );
    }
  };

  const handleDeleteSubmit = async (
    videoIds: string[],
    companyVideos: boolean = false
  ) => {
    await deleteVideos({ videoIds, deleteMultiple: true, companyVideos });
  };

  const handleDeleteWithROSubmit = async (videoId: string) => {
    try {
      await deleteVideoWithRO(videoId);
      successToast({
        title: `Video successfully moved to recently deleted bin`,
      });
    } catch (_) {}
  };

  const handleArchiveSubmit = async (videoIds: number[]) => {
    updateVideoArchiveStatus({
      videoIds,
      isArchived: true,
    });
  };

  const deleteSelectedVideos = async () => {
    if (isVideoWithRO) {
      await handleDeleteWithROSubmit(
        (video[0] as VideoListAutomotiveItem)?.videoRequest?.videoRequestId
      );

      return;
    }
    if (isMultipleVideos) {
      await handleDeleteSubmitSelected(video as VideoListAutomotiveItem[]);
      return;
    }
    await handleDeleteSubmit([video[0].id]);

    handleModalClose();
  };

  return (
    <NewModal
      headerText={
        isCompanyVideo
          ? t('Are you sure you want to delete this company video?')
          : t(
              'Are you sure you want to move this video to recently deleted bin?'
            )
      }
      headerTextStyle={{
        fontSize: theme.fontSizes.lg,
        lineHeight: '24px',
        color: theme.palette.themeDark,
      }}
      closeModalOnBackdropOrEscPress={true}
      closeModal={() => {
        handleModalClose();
      }}
      style={{
        content: {
          padding: '32px',
          minWidth: 'auto',
        },
      }}
    >
      <Content>{!isCompanyVideo && content}</Content>
      <Footer>
        <Button
          text={areAllVideosArchived ? t('Cancel') : t('Delete')}
          variant='secondary'
          onClick={async () => {
            if (areAllVideosArchived) {
              handleModalClose();
              return;
            }
            deleteGTMHandler(GTMVideoInteractionActions.DELETE);
            await deleteSelectedVideos();
          }}
        />
        <Button
          text={areAllVideosArchived ? t('Delete') : t('Archive')}
          onClick={async () => {
            deleteGTMHandler(
              areAllVideosArchived
                ? GTMVideoInteractionActions.DELETE
                : GTMVideoInteractionActions.ARCHIVE
            );

            if (areAllVideosArchived) {
              deleteSelectedVideos();
              return;
            }
            setIsDisabled(true); /** SUS-973 changes **/
            handleArchiveSubmit(
              video.map((videoItem: VideoListAutomotiveItem | VideoData) =>
                Number(videoItem.id)
              )
            );
            handleModalClose();
          }}
          disabled={isDisabled} /** SUS-973 changes **/
        />
      </Footer>
    </NewModal>
  );
};
